import React, { useState } from "react";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { PlusIcon } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTableRowActions } from "./data-table-row-actions";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { DataTableViewOptions } from "./data-table-view-options";
import {
  useGetCoachesQuery,
  useUpdateCoachMutation,
  useLazyGetAllCoachForExportQuery,
} from "@/services/coachApi";
import { Coach, ErrorType } from "@/types";
import CoachForm from "../modal/form";
import TableFilters from "@/components/ui/table/data-table-filter";
const { VITE_VIEW_S3_URL } = import.meta.env;
import {
  coachMapper,
  displayDate,
  displayDateTime,
  displayValue,
  downloadCSV,
} from "@/lib/helper";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import usePagination from "@/hooks/use-pagination";
import Pagination from "@/components/ui/table/pagination-table";
import { UserStatus } from "@/types/enums";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import useTableData from "@/components/ui/table/data-table-logic";
const status = [
  { value: "active", label: "Active", color: "bg-green-500" },
  { value: "inactive", label: "Inactive", color: "bg-blue-500" },
  { value: "pending", label: "Pending", color: "bg-orange-500", hide: true },
];

interface SearchCriteriaType {
  limit: number;
  offset: number;
  sort_order: string;
  sort_key: string;
  status?: boolean;
  search_key?: string;
  type: string;
}

const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
  type: "coach",
};

export default function CoachTableView() {
  const coach = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).coach ??
        "no_access"
      );
    } catch {
      return "no_access";
    }
  })();
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;
  const { searchCriteria, query, toggleSortOrder, setSearchCriteria, inputValue, setInputValue } =
    useTableData(initialValue);
  const orgName = useSelector(
    (state: RootState) => state.auth.userInfo?.user?.org_name
  );

  // search input
  const [openFilter, setOpenFilter] = useState(false);
  const [filterData, setFilter] = useState({});

  const {
    data: coachData,
    isLoading,
    refetch,
    error,
  } = useGetCoachesQuery(
    { org_id: orgId, query: query },
    {
      skip: query == "",
    }
  );

  const navigate = useNavigate();
  const [updateCoach] = useUpdateCoachMutation();

  const coachTableData = React.useMemo(() => {
    return Array.isArray(coachData?.data) ? coachData.data : [];
  }, [coachData]);
  const { toast } = useToast();


  const [sorting, setSorting] = useState<SortingState>([]);
  const [filterID, setFilterID] = useState({});

  const [filters, setFilters] = useState<"">();
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [isClear, setIsClear] = useState(false);
  const [clearValue, setIsClearValue] = useState({});
  const [selectAllRows, setSelectAllRows] = useState(false);
  const [showSelectAllNotification, setShowSelectAllNotification] = useState(false);
  const [getAllCoachForExport] = useLazyGetAllCoachForExportQuery();

  const handleExportSelected = async () => {
    if (selectAllRows) {
      // Export all rows
      try {
        const filterQuery = Object.keys(filterData).length > 0 
          ? Object.entries(filterData)
              .filter(([_, value]) => value !== "")
              .map(([key, value]) => `${key}=${value}`)
              .join("&")
          : "";
        
        const result = await getAllCoachForExport({ 
          org_id: orgId, 
          filterQuery: filterQuery 
        }).unwrap();
        
        console.log("result", {result});
        
        if (result?.data && result.data.length > 0) {
          downloadCSV(result.data, "all_coaches_list.csv", coachMapper);
          toast({
            variant: "success",
            title: `Exported ${result.data.length} coaches successfully`,
          });
        } else {
          toast({
            variant: "destructive",
            title: "No data to export",
            description: "No coach records found to export.",
          });
        }
      } catch (error) {
        console.error("Export failed:", error);
        toast({
          variant: "destructive",
          title: "Export failed",
          description: "Failed to export all coaches. Please try again.",
        });
      }
    } else {
      // Export selected rows
      const selectedRows = table
        .getSelectedRowModel()
        .rows.map((row) => row.original);
      if (selectedRows.length === 0) {
        toast({
          variant: "destructive",
          title: "Please select one or more records to perform this action.",
        });
        return;
      }
      downloadCSV(selectedRows, "coach_list.csv", coachMapper);
    }
  };

  const handleStatusChange = async (payload: Coach & { id: number }) => {
    try {

      const resp = await updateCoach(payload).unwrap();
      refetch();
      if (resp) {
        toast({
          variant: "success",
          title: "Coach Updated Successfully",
        });
      }
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `${typedError.data?.detail || typedError.data?.message}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `Something Went Wrong.`,
        });
      }
    }
  };

  const actionsColumn: ColumnDef<Coach> = {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => (
      <DataTableRowActions
        access={coach}
        data={row.original}
        refetch={refetch}
        handleEdit={handleOpenForm}
      />
    ),
  };

  const columns: ColumnDef<Coach>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value: any) =>
            table.toggleAllPageRowsSelected(!!value)
          }
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value: any) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "own_coach_id",
      meta: "Coach Id",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Coach Id"
          sortKey="id"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            <div className="">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p className="capitalize cursor-pointer">
                      {/* Display the truncated name */}
                      {displayValue(
                        `${row.original.own_coach_id}`.length > 12
                          ? `${row.original.own_coach_id}`.substring(0, 12) +
                          "..."
                          : `${row.original.own_coach_id}`
                      )}
                    </p>
                  </TooltipTrigger>
                  <TooltipContent>
                    {/* Display the full name in the tooltip */}
                    <p className="capitalize text-sm">
                      {displayValue(`${row.original.own_coach_id}`)}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>{" "}
          </div>
        );
      },
    },
    {
      accessorFn: (row) => `${row.first_name} ${row.last_name}`,
      id: "full_name",
      meta: "Coach Name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Coach Name"
          sortKey="first_name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            <div className="">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p className="capitalize cursor-pointer">
                      {/* Display the truncated name */}
                      {displayValue(
                        `${row.original.first_name} ${row.original.last_name}`
                          .length > 8
                          ? `${row.original.first_name} ${row.original.last_name}`.substring(
                            0,
                            8
                          ) + "..."
                          : `${row.original.first_name} ${row.original.last_name}`
                      )}
                    </p>
                  </TooltipTrigger>
                  <TooltipContent>
                    {/* Display the full name in the tooltip */}
                    <p className="capitalize">
                      {displayValue(
                        `${row.original.first_name} ${row.original.last_name}`
                      )}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        );
      },
    },
    {
      accessorFn: (row) => `${row.email}`,
      id: "email",
      meta: "email",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Email"
          sortKey="id"
          sortable={false}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            <div className="">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p className="normal-case cursor-pointer">
                      {/* Display the truncated name */}
                      {displayValue(
                        `${row.original.email}`.length > 15
                          ? `${row.original.email}`.substring(0, 15) + "..."
                          : `${row.original.email}`
                      )}
                    </p>
                  </TooltipTrigger>
                  <TooltipContent>
                    {/* Display the full name in the tooltip */}
                    <p className="normal-case">
                      {displayValue(`${row.original.email}`)}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        );
      },
    },
    {
      accessorFn: (row) => `${row.mobile_number}`,
      id: "mobile_number",
      meta: "mobile_number",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Phone Number"
          sortKey="id"
          sortable={false}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            <div className="">
              <p className="capitalize cursor-pointer">
                {/* Display the truncated name */}
                {displayValue(`${row.original.mobile_number}`, true)}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "activated_on",
      meta: "Activation Date",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Activation Date"
          sortKey="activated_on"
          sortable={true}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayDate(row?.original.activated_on)}
          </div>
        );
      },
    },
    {
      accessorKey: "coach_status",
      meta: "Status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Status"
          sortKey="coach_status"
          sortable={true}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const value =
          row.original?.coach_status != null
            ? row.original?.coach_status + ""
            : "pending";

        const statusLabel = status.filter((r) => r.value === value)[0];

        return (
          <Select
            value={value}
            onValueChange={(e: any) => {
              if (row.original.id) {
                handleStatusChange({ ...row.original, coach_status: e, id: row.original.id })
              }
            }}
            disabled={value == "pending" || coach == "read"}
          >
            <SelectTrigger className="h-8">
              <SelectValue placeholder="Status" className="text-gray-400 ">
                <span className="flex gap-2 items-center">
                  <span
                    className={`${statusLabel?.color} rounded-[50%] w-4 h-4`}
                  ></span>
                  <span>{statusLabel?.label}</span>
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {status.map(
                (item: any) =>
                  !item.hide && (
                    <SelectItem key={item.value + ""} value={item.value + ""}>
                      {item.label}
                    </SelectItem>
                  )
              )}
            </SelectContent>
          </Select>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "check_in",
      meta: "Last Check In",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Last Check In"
          sortKey="check_in"
          sortable={true}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),

      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayDateTime(row?.original.check_in)}
          </div>
        );
      },
    },
    {
      accessorKey: "last_online",
      meta: "Last Login",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Last Login"
          sortKey="last_online"
          sortable={true}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayDateTime(row?.original.last_online)}
          </div>
        );
      },
    },
    ...(coach !== "read" ? [actionsColumn] : []),
  ];
  const table = useReactTable({
    data: coachTableData as Coach[],
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });

  // Handle showing select all notification
  React.useEffect(() => {
    const allPageRowsSelected = table.getIsAllPageRowsSelected();
    const selectedRowCount = table.getSelectedRowModel().rows.length;
    const totalRowsInCurrentPage = table.getRowModel().rows.length;
    
    if (allPageRowsSelected && selectedRowCount === totalRowsInCurrentPage && totalRowsInCurrentPage > 0 && !selectAllRows) {
      setShowSelectAllNotification(true);
    } else if (selectedRowCount === 0 || selectAllRows) {
      setShowSelectAllNotification(false);
    }
  }, [table.getIsAllPageRowsSelected(), table.getSelectedRowModel().rows.length, selectAllRows]);

  // Reset select all when row selection changes
  React.useEffect(() => {
    if (Object.keys(rowSelection).length === 0) {
      setSelectAllRows(false);
      setShowSelectAllNotification(false);
    }
  }, [rowSelection]);

  // Auto-select all visible rows when selectAllRows is true and data changes
  React.useEffect(() => {
    if (selectAllRows && coachTableData.length > 0) {
      table.toggleAllPageRowsSelected(true);
    }
  }, [selectAllRows, coachTableData, table]);

  function handleFilterChange(field: string, value: string | number) {
    setFilter((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  const filterDisplay = [
    {
      type: "select",
      name: "status",
      label: "Status",
      options: [
        { id: "pending", name: "Pending" },
        { id: "inactive", name: "Inactive" },
        { id: "active", name: "Active" },
      ],
      function: (value: string) => handleFilterChange("status", value),
    },
  ];

  const totalRecords = coachData?.filtered_counts || 0;
  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination<SearchCriteriaType>({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  const handleOpenForm = (coachData: Coach | null = null) => {
    const data = coachData ? {
      ...coachData,
      member_ids: coachData.member_ids ?? [],
    } as Coach : null;
    setEditCoach(data);
    setOpen(true);
  };

  const handleAddForm = () => {
    setEditCoach(null);
    setOpen(true);

  }

  const [open, setOpen] = useState<boolean>(false);
  const [editCoach, setEditCoach] = useState<Coach | null>(null);

  const handleSelectAllRows = () => {
    setSelectAllRows(true);
    setShowSelectAllNotification(false);
  };

  const handleClearSelection = () => {
    setSelectAllRows(false);
    setShowSelectAllNotification(false);
    table.toggleAllPageRowsSelected(false);
    setRowSelection({});
  };

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-row items-center justify-between gap-2 px-3">
        {/* Table Filter component */}
        <div className="flex-initial sm:flex-1 min-w-0">
          <TableSearchFilter setSearchCriteria={setSearchCriteria} inputValue={inputValue} setInputValue={setInputValue} />
        </div>

        {/* Buttons Container */}
        <div className="flex flex-row items-center gap-1 sm:gap-2 shrink-0">
          <Button
            access={coach !== "read"}
            className="bg-primary text-sm text-black flex items-center justify-center h-8 px-2 min-w-[32px]"
            onClick={handleAddForm}
          >
            <PlusIcon className="size-4" />
            <span className="hidden sm:inline ml-1">Create New</span>
          </Button>

          <DataTableViewOptions table={table} action={handleExportSelected} />

          <button
            className={`border rounded-full size-8 text-gray-400 flex items-center justify-center shrink-0 ${Object.keys(filterData).length ? "border-primary" : ""}`}
            onClick={() => setOpenFilter(true)}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <i
                    className={`fa fa-filter ${Object.keys(filterData).length ? "text-primary" : ""}`}
                  ></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>click to apply filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>
        </div>
      </div>

      {/* Gmail-style Select All Notification */}
      {(showSelectAllNotification || selectAllRows) && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mx-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="text-sm text-blue-800">
              {selectAllRows ? (
                <>
                  All <strong>{totalRecords}</strong> coaches are selected.
                </>
              ) : (
                <>
                  All <strong>{table.getSelectedRowModel().rows.length}</strong> coaches on this page are selected.
                </>
              )}
            </div>
            {!selectAllRows && (
              <button
                onClick={handleSelectAllRows}
                className="text-blue-600 hover:text-blue-800 underline text-sm font-medium"
              >
                Select all {totalRecords} coaches
              </button>
            )}
            {selectAllRows && (
              <button
                onClick={handleClearSelection}
                className="text-blue-600 hover:text-blue-800 underline text-sm font-medium"
              >
                Clear selection
              </button>
            )}
          </div>
        </div>
      )}

      {/* Mobile Card View */}
      <div className="block md:hidden space-y-4 px-3">
        {isLoading ? (
          <div className="flex space-x-2 justify-center items-center h-24">
            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
            <div className="size-3 bg-black rounded-full animate-bounce"></div>
          </div>
        ) : coachTableData.length > 0 ? (
          coachTableData.map((row: any) => (
            <div
              key={row.id}
              className="bg-white border rounded-lg p-4 shadow-sm relative space-y-2"
              onClick={() => coach !== "read" && handleOpenForm(row)}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-bold text-lg capitalize">{row.first_name} {row.last_name}</h3>
                  <p className="text-sm text-gray-500 uppercase">{row.own_coach_id}</p>
                </div>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <div className={`w-3 h-3 rounded-full ${status.find(s => s.value === (row.coach_status ?? "pending"))?.color || "bg-gray-400"}`}></div>
                  <DataTableRowActions
                    access={coach}
                    data={row}
                    refetch={refetch}
                    handleEdit={handleOpenForm}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm pt-2">
                <div className="flex items-center gap-2">
                  <i className="fa-regular fa-envelope text-gray-400 w-4"></i>
                  <span className="truncate">{row.email || "N/A"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-phone text-gray-400 w-4"></i>
                  <span>{row.mobile_number || "N/A"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-regular fa-calendar-check text-gray-400 w-4"></i>
                  <span>Active: {displayDate(row.activated_on)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-door-open text-gray-400 w-4"></i>
                  <span className="truncate">Check-in: {displayDateTime(row.check_in)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-right-to-bracket text-gray-400 w-4"></i>
                  <span className="truncate">Login: {displayDateTime(row.last_online)}</span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="h-24 text-center flex items-center justify-center border rounded-lg">
            No coaches found.
          </div>
        )}
      </div>

      <div className="hidden md:block rounded-none border border-border ">
        <ScrollArea className="w-full relative" hideScrollToTop={true}>
          <ScrollBar
            orientation="horizontal"
            className="relative z-30 cursor-grab h-2"
          />
          {/* <Table className="w-full overflow-x-scroll"> */}
          <Table className="w-full sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]  ">
            {/* //overflow-y-auto scrollbar-thin */}
            <TableHeader className="bg-secondary">
              {table?.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    <div className="flex space-x-2 justify-center items-center bg-white ">
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={(e) => {
                      if (coach !== "read" && (e.target instanceof Element &&
                        !e.target.closest('[role="checkbox"]') && (e.target === e.currentTarget || e.target.closest('td')))) {
                        handleOpenForm(row.original as Coach)
                      }
                    }}
                    className={coach !== "read" ? "cursor-pointer hover:bg-secondary" : ""}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : coachTableData.length > 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No coaches added yet.
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No coaches found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {/* pagination */}
      {coachTableData.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}

      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilter}
        setSearchCriteria={setSearchCriteria}
        filterDisplay={filterDisplay}
        setInputValue={setInputValue}
      />

      <CoachForm
        coachData={editCoach}
        setCoachData={setEditCoach}
        setOpen={setOpen}
        open={open}
        refetch={refetch}
      />
    </div>
  );
}